import React, { useEffect, useRef, useState, useMemo, forwardRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import defaultStyles from './AggregateConflictTable.module.css';
import jlrStyles from './AggregateConflictTableJlr.module.css';
import { AutoSizer, List } from 'react-virtualized';
import { AuthChecker, Button } from '../../atomicComponents';
import { Popover, LinearProgress, withStyles } from '@material-ui/core';
import { columnsPropType } from '../../types';
import { SelectionMenu, TableRow, TableHead } from '../../atomicComponents';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { setStylesAccordingToPlant } from '../../utils/utils';
import { JLR_PLANT } from '../../constants';

const AggregateConflictTable = ({
  rows: data,
  columns: originalColumns,
  classNames = {},
  rowHeight = 38,
  headerHeight,
  defaultColumns: originalDefaultColumns,
  primaryAction,
  secondaryActions = [],
  downloadActions = [],
  setHasActiveEdits,
  setAsnCount,
  isDataLoading,
  displayHash,
  hideCheckbox,
  editConflictCount,
  isRenderEditQtyStatus,
}) => {
  const [plant] = usePlantAndLinesStore().plantState;
  const [styles, setStyles] = useState(defaultStyles);

  const [rowsData, setRowsData] = useState({
    data: [...data],
    filteredData: [...data],
  });
  const [fieldToSearchTextMap, setFieldToSearchTextMap] = useState({});
  const [isEllipsisPopupOpen, setIsEllipsisPopupOpen] = useState(false);
  const [ellipsisAnchorEl, setEllipsisAnchorEl] = useState(null);
  const [ellipsisPopupValue, setEllipsisPopupValue] = useState(null);
  const [fieldToFiltersSetMap, setFieldToFiltersSetMap] = useState({});

  const closeEllipsisPopup = () => setIsEllipsisPopupOpen(false);

  const grandTotal = 'Grand Total';

  const SummaryRow = forwardRef(({ style, children }, ref) => (
    <div
      style={style}
      ref={ref}
      className={styles.summaryContainer}
      data-testid={grandTotal}
    >
      <div className={styles.summary}>{children}</div>
    </div>
  ));

  const vListRef = useRef(null);
  const tableHeadRef = useRef(null);
  const leftSummaryRowRef = useRef(null);

  const handleListScroll = e =>
    (tableHeadRef.current.scrollLeft = e.target.scrollLeft);

  useEffect(() => {
    vListRef.current = document.getElementsByClassName(
      'ReactVirtualized__Grid ReactVirtualized__List',
    )[0];

    if (vListRef.current)
      vListRef.current.addEventListener('scroll', handleListScroll);
    vListRef.current.addEventListener('scroll', e =>
      handleListScroll(e, leftSummaryRowRef),
    );
    return () =>
      vListRef.current.removeEventListener('scroll', handleListScroll);
  }, []);

  const handlePopoverOpen = (event, val) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setEllipsisPopupValue(val);
      setIsEllipsisPopupOpen(true);
      setEllipsisAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setIsEllipsisPopupOpen(false);
    setEllipsisAnchorEl(null);
  };

  const toggleSelectAllRows = () =>
    setRowsData(prev => {
      const visibleRowIdsSet = new Set(prev.filteredData.map(row => row.id));
      const isSelected = !prev.filteredData.every(row => row.isSelected);
      return {
        ...prev,
        filteredData: prev.filteredData.map(row => ({ ...row, isSelected })),
        data: prev.data.map(row => {
          if (visibleRowIdsSet.has(row.id)) return { ...row, isSelected };
          return row;
        }),
      };
    });

  const handleRowSelection = selectedId => {
    setRowsData(prev => ({
      ...prev,
      data: prev.data.map(row =>
        row.id === selectedId ? { ...row, isSelected: !row.isSelected } : row,
      ),
    }));
  };

  useEffect(() => {
    if (setHasActiveEdits) {
      const areEditsActive = rowsData.data.some(row => row.isSelected);
      setHasActiveEdits(areEditsActive);
    }
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    // TODO: Fix this
    setAsnCount(rowsData.filteredData.length);
  }, [setAsnCount, rowsData]);

  const plantName = useMemo(() => plant.label, [plant.label]);

  const defaultColumns = useMemo(
    () => {
      return [
        {
          title: '#',
          field: '_serialNumber',
          width: 54,
          render: (_, idx) => idx,
          renderHeader: rows =>
            displayHash ? (
              '#'
            ) : (
              <div
                className={
                  JLR_PLANT === plantName
                    ? styles.jlrasnsCount
                    : styles.asnsCount
                }
                data-testid="count"
              >
                {displayHash ? '#' : rows?.length || 0}
              </div>
            ),
          headerStyles: {
            paddingLeft: 7,
            paddingRight: 2,
          },
        },
        (primaryAction || secondaryActions?.length) && !hideCheckbox
          ? {
              title: 'select',
              field: '_select',
              width: 30,
              renderHeader: rows => {
                return (
                  <input
                    type="checkbox"
                    onChange={toggleSelectAllRows}
                    checked={rows?.length && rows.every(row => row.isSelected)}
                  />
                );
              },
              render: ({ isSelected, id }) => (
                <input
                  style={{ marginTop: 0, marginBottom: 0 }}
                  type="checkbox"
                  onChange={() => handleRowSelection(id)}
                  checked={!!isSelected}
                  data-testid={`select-row-${id}`}
                  id={`select-row-${id}`}
                  name={`select-row-${id}`}
                />
              ),
            }
          : null,
        ...originalDefaultColumns,
      ].filter(i => i);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originalDefaultColumns, plant.label, styles],
  );
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  const noOfFixedColumns = primaryAction || secondaryActions?.length ? 2 : 1;

  const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] = useState(
    false,
  );
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const customizeTableButtonRef = useRef(null);

  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderHeaders = result => {
    // dropped outside the list or dropped at serial number place
    if (!result.destination || result.destination.index < noOfFixedColumns) {
      return;
    }

    const reorderedColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index,
    );

    setSelectedColumns(reorderedColumns);
  };

  const rowRenderer = ({ style, index, key }) => {
    const handleCopy = text => {
      navigator.clipboard.writeText(text);
      setShowCopyAlert(true);
    };

    return (
      <TableRow
        index={index}
        style={style}
        key={key}
        row={rowsData.filteredData[index]}
        columns={selectedColumns}
        classNames={{ ...classNames }}
        rowHeight={rowHeight}
        onCopy={handleCopy}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
      />
    );
  };

  const handleColumnSelectionChange = columnTitle => {
    if (selectedColumns.map(({ title }) => title).includes(columnTitle))
      return setSelectedColumns(prev =>
        prev.filter(column => column.title !== columnTitle),
      );
    return setSelectedColumns(prev => [
      ...prev,
      ...originalColumns.filter(({ title }) => title === columnTitle),
    ]);
  };

  const applyColumnsSelections = columnSelections => {
    setSelectedColumns([
      ...defaultColumns.slice(
        0,
        defaultColumns.length - originalDefaultColumns.length,
      ),
      ...originalColumns.filter(col => columnSelections.includes(col.title)),
    ]);
  };

  useEffect(() => {
    if (rowsData?.filteredData) agsnConflictCountList(rowsData?.filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsData?.filteredData]);

  useEffect(() => {
    if (showCopyAlert) setShowCopyAlert(false);
  }, [showCopyAlert]);

  useDeepCompareColumnArraysEffect(() => {
    setSelectedColumns(defaultColumns);
  }, [defaultColumns]);

  useEffect(() => {
    setRowsData(prevState => {
      const dataFilteredBySearchTexts = filterBySearchTexts(
        fieldToSearchTextMap,
        prevState.data,
        selectedColumns,
      );

      const dataFilteredBySearchAndFilters = filterBySelectedFilters(
        fieldToFiltersSetMap,
        dataFilteredBySearchTexts,
        selectedColumns,
      );

      return { ...prevState, filteredData: dataFilteredBySearchAndFilters };
    });
  }, [
    selectedColumns,
    fieldToFiltersSetMap,
    fieldToSearchTextMap,
    rowsData.data,
  ]);

  useEffect(() => {
    setRowsData({
      data: [...data],
      filteredData: [...data],
    });
  }, [data]);

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  const CustomizedLinearProgress = withStyles(() => ({
    colorPrimary: {
      backgroundColor: plant.label === JLR_PLANT ? '#beedc4' : 'primary',
    },
    bar: {
      backgroundColor: plant.label === JLR_PLANT ? '#26662E' : 'primary',
    },
  }))(LinearProgress);

  const [grandTotalConflictCount, setGrandTotalConflictCount] = useState([]);
  const lineWiseConflict = [];

  const childMaterialsQuantities = lineNameList => {
    return lineNameList?.map(asns =>
      asns.lineAndAllocatedProducedChildMaterialsQuantities?.map(asnList => [
        lineWiseConflict.push(asnList),
      ]),
      lineWiseConflict.sort((a,b) => (a.lineName > b.lineName) ? 1 : ((b.lineName > a.lineName) ? -1 : 0))                       
    );
  };
  const agsnConflictCountList = async lineNameList => {
    if (lineNameList.length > 0) {
      const allocatedConflictsToALine = [];
      childMaterialsQuantities(lineNameList);
      lineWiseConflict.forEach(item => {
        if (allocatedConflictsToALine) {
          allocatedConflictsToALine.push({
            lineName: item.lineName,
            allocatedProducedMaterialsQuantity:
              item.allocatedProducedMaterialsQuantity,
          });
        }
      });

      const uniqueLines = {
        lineNames: Array.from(
          new Set(lineWiseConflict?.map(row => row.lineName)),
        ),
      };
      const finalLinesConflictData = [];
      for (let j = 0; j < uniqueLines.lineNames.length; j++) {
        const conflictCounts = allocatedConflictsToALine
          .filter(item => item.lineName === uniqueLines.lineNames[j])
          .reduce(
            (previousScore, red) =>
              previousScore + red.allocatedProducedMaterialsQuantity,
            0,
          );
        finalLinesConflictData.push({
          lineName: uniqueLines.lineNames[j],
          totalConflictCount: conflictCounts,
        });
      }
      setGrandTotalConflictCount(finalLinesConflictData);
    }
  };

  return (
    <>
      <div
        style={{ width: '100%', height: '100%' }}
        className={clsx(
          styles.baseContainer,
          classNames.tableContainerClassName,
        )}
      >
        <div
          style={{ width: '100%' }}
          className={clsx(
            styles.basicTableContainer,
            classNames.tableContainerClassName,
          )}
        >
          <AutoSizer>
            {({ width: windowWidth, height: windowHeight }) => {
              let width = windowWidth;
              let height = windowHeight;
              const listHeight = height - 2 * rowHeight;
              const leftTableWidth = (width / 100) * 100;
              return (
                <div
                  style={{
                    height,
                    width,
                  }}
                >
                  <div
                    className={styles.basicTable}
                    style={{
                      height,
                      width: leftTableWidth,
                    }}
                  >
                    <TableHead
                      columns={selectedColumns}
                      ref={tableHeadRef}
                      className={classNames.tableHeadClassName}
                      height={headerHeight || rowHeight}
                      reorderHeaders={reorderHeaders}
                      rows={rowsData.filteredData}
                      allRows={rowsData.data}
                      fieldToSearchTextMap={fieldToSearchTextMap}
                      setSearchTextForTitleCurry={field => value =>
                        setFieldToSearchTextMap(prev => ({
                          ...prev,
                          [field]: value.toLowerCase(),
                        }))}
                      noOfFixedColumns={noOfFixedColumns}
                      fieldToFiltersSetMap={fieldToFiltersSetMap}
                      setFiltersForFieldCurry={field => selectedFilters =>
                        setFieldToFiltersSetMap(prev => ({
                          ...prev,
                          [field]: new Set(selectedFilters),
                        }))}
                    />
                    <List
                      ref={vListRef}
                      containerStyle={{ overflow: 'initial' }}
                      rowRenderer={rowRenderer}
                      height={listHeight}
                      rowHeight={rowHeight}
                      rowCount={rowsData.filteredData.length}
                      width={width}
                      className={clsx(
                        styles.scrollBar,
                        styles.heightAdjustedListScroll,
                      )}
                      overscanRowCount={10}
                    />
                    <SummaryRow
                      dataTestId="grandTotal"
                      style={{
                        height: rowHeight,
                        width: leftTableWidth,
                      }}
                      ref={leftSummaryRowRef}
                    >
                      <div className={styles.grandTotal}>
                        <span className={styles.conflictCount}>
                          {grandTotal}
                        </span>
                      </div>
                      {grandTotalConflictCount?.map((rowValue, index) => {
                        if (rowsData?.filteredData.length > 0) {
                          return (
                            <div className={styles.lineWiseConflictCount}>
                              <div className={styles.lineNames}>
                                <span
                                  data-testid={`grand-total-line-row-${rowValue.lineName}`}
                                >
                                  {rowValue.lineName}
                                </span>
                                <span className={styles.conflictCount}>
                                  {!isRenderEditQtyStatus
                                    ? rowValue.totalConflictCount
                                    : editConflictCount[index]}
                                </span>
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div className={styles.lineWiseConflictCount} />
                          );
                        }
                      })}
                    </SummaryRow>
                  </div>
                  {isDataLoading && (
                    <div>
                      <CustomizedLinearProgress />
                    </div>
                  )}
                </div>
              );
            }}
          </AutoSizer>
        </div>
        <div className={styles.actionsBar}>
          <Button
            className={styles.actionButton}
            variant="tertiary"
            onClick={() => setIsCustomizeTableMenuOpen(true)}
            ref={customizeTableButtonRef}
          >
            CUSTOMIZE TABLE
          </Button>
          {downloadActions.map(action => (
            <Button
              variant="primary"
              className={styles.actionButton}
              onClick={() =>
                action.actionFn(
                  action.getFilteredData
                    ? rowsData.filteredData
                    : rowsData.data.filter(row => row.isSelected),
                )
              }
              disabled={action.isDisabled}
            >
              {action.name}
            </Button>
          ))}
          {secondaryActions.map(action => (
            <AuthChecker operation={action.authOperation}>
              {isAuthorized => (
                <Button
                  variant="primary"
                  className={styles.actionButton}
                  style={{
                    backgroundColor: !(
                      action.authOperation &&
                      (!isAuthorized ||
                        (action.shouldEnable !== undefined &&
                          !action.shouldEnable(
                            rowsData.data.filter(row => row.isSelected),
                          )))
                    )
                      ? action.backgroundColor
                      : null,
                    color: !(
                      action.authOperation &&
                      (!isAuthorized ||
                        (action.shouldEnable !== undefined &&
                          !action.shouldEnable(
                            rowsData.data.filter(row => row.isSelected),
                          )))
                    )
                      ? action.textColor
                      : null,
                  }}
                  onClick={() =>
                    action.actionFn(
                      action.getFilteredData
                        ? rowsData.filteredData
                        : rowsData.data.filter(row => row.isSelected),
                    )
                  }
                  disabled={
                    action.authOperation &&
                    (!isAuthorized ||
                      (action.shouldEnable !== undefined &&
                        !action.shouldEnable(
                          rowsData.data.filter(row => row.isSelected),
                        )) ||
                      (action.enableButton !== undefined &&
                        !action.enableButton))
                  }
                >
                  {action.name}
                </Button>
              )}
            </AuthChecker>
          ))}
          {Object.values(fieldToFiltersSetMap).every(
            filtersSet => !filtersSet?.size,
          ) || (
            <Button
              className={clsx(styles.actionButton, styles.clearFiltersButton)}
              variant="secondary"
              disabled={Object.values(fieldToFiltersSetMap).every(
                filtersSet => !filtersSet?.size,
              )}
              onClick={() => setFieldToFiltersSetMap({})}
            >
              Clear Filters
            </Button>
          )}
          {primaryAction && (
            <AuthChecker operation={primaryAction.authOperation}>
              {isAuthorized => (
                <Button
                  className={clsx(
                    styles.actionButton,
                    styles.primaryActionButton,
                    primaryAction.className || null,
                  )}
                  variant="primary"
                  onClick={() =>
                    primaryAction.actionFn(
                      rowsData.data.filter(row => row.isSelected),
                    )
                  }
                  disabled={
                    !isAuthorized ||
                    (primaryAction.shouldEnable !== undefined &&
                      !primaryAction.shouldEnable(
                        rowsData.data.filter(row => row.isSelected),
                      )) ||
                    (primaryAction.enableButton !== undefined &&
                      !primaryAction.enableButton)
                  }
                  data-testid="primary-action"
                >
                  {primaryAction.name}
                </Button>
              )}
            </AuthChecker>
          )}
        </div>
      </div>
      <SelectionMenu
        anchorEl={customizeTableButtonRef.current}
        open={isCustomizeTableMenuOpen}
        handleClose={() => setIsCustomizeTableMenuOpen(false)}
        options={originalColumns.map(({ title }) => title)}
        currentSelections={selectedColumns
          .filter(({ title }) => title !== 'select' && title !== '#')
          .map(({ title }) => title)}
        handleChange={handleColumnSelectionChange}
        resetToDefault={() => setSelectedColumns([...defaultColumns])}
        applySelections={applyColumnsSelections}
        defaultColumns={defaultColumns.map(({ title }) => title)}
      />
      <Popover
        open={isEllipsisPopupOpen}
        anchorEl={ellipsisAnchorEl}
        onClose={closeEllipsisPopup}
        className={styles.popover}
        classes={{
          paper: styles.popoverPaper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {ellipsisPopupValue}
      </Popover>
      <div
        className={clsx(
          styles.copyAlert,
          showCopyAlert ? styles.visible : styles.hidden,
        )}
      >
        Text copied
      </div>
    </>
  );
};

AggregateConflictTable.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
  }),
  row: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
    }),
  ),
  columns: columnsPropType,
  rowHeight: PropTypes.number,
  headerHeight: PropTypes.number,
  defaultColumns: columnsPropType,
  primaryAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    }),
  ),
  setHasActiveEdits: PropTypes.func,
  isDataLoading: PropTypes.bool,
  displayHash: PropTypes.bool,
  hideCheckbox: PropTypes.bool,
};

export default AggregateConflictTable;

function filterBySearchTexts(fieldToSearchTextMap, data, columns) {
  const fieldToSearchFnMap = columns.reduce((acc, col) => {
    if (col.searchFn) return { ...acc, [col.field]: col.searchFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToSearchTextMap).forEach(entry => {
    const [field, searchText] = entry;
    const defaultSearchFn = (fieldValue, searchValue) =>
      fieldValue.toLowerCase().startsWith(searchValue);

    const searchFn = fieldToSearchFnMap[field] || defaultSearchFn;

    filteredData = filteredData.filter(
      row => !searchText || !row[field] || searchFn(row[field], searchText),
    );
  });
  return filteredData;
}

function filterBySelectedFilters(fieldToFiltersSetMap, data, columns) {
  const fieldToFilterFnMap = columns.reduce((acc, col) => {
    if (col.filterFn) return { ...acc, [col.field]: col.filterFn };
    return acc;
  }, {});

  let filteredData = [...data];
  Object.entries(fieldToFiltersSetMap).forEach((entry, idx) => {
    const [field, filtersSet] = entry;
    const filterFn = fieldToFilterFnMap[field];

    filteredData = filteredData.filter(row => {
      if (!filterFn) return !filtersSet?.size || filtersSet.has(row[field]);
      if (!idx) {
        console.log(' [...filtersSet]', [...filtersSet]);
      }

      return (
        !filtersSet?.size ||
        [...filtersSet].some(filter => filterFn(row, filter.value))
      );
    });
  });
  return filteredData;
}

function useDeepCompareColumnArraysEffect(callback, dependencies) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareColumnArraysMemoize));
}

function useDeepCompareColumnArraysMemoize(value) {
  const ref = useRef();
  if (!deepCompareEqualsColumnsArray(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function deepCompareEqualsColumnsArray(oldArray, newArray) {
  if (oldArray?.length !== newArray?.length) return false;
  const zippedArray = oldArray.map((e, i) => [e, newArray[i]]);
  return zippedArray.every(tuple => tuple[0].title === tuple[1].title);
}
